import { useQuery } from '@tanstack/react-query';
import { useMemo, useEffect } from 'react';
import { useUserIdentityState } from '@provider/user-identity';
import { useLoaderState } from '@utils/react';
import { getComponentEntriesQuery } from '../../api/query.get-component-entries.js';
import { useComponentEntrySelector, componentEntryStore } from '../../state.js';

function useFilterValues() {
  const user = useUserIdentityState(state => state.user);
  const filters = useComponentEntrySelector(state => state.filters);
  const {
    data,
    isLoading,
    isRefetching
  } = useQuery(getComponentEntriesQuery(user, filters.timespan));
  const options = useMemo(() => data?.reduce((acc, curr) => {
    const hasAddedProduct = acc.products.some(it => it.id === curr.product?.id);
    if (!hasAddedProduct && curr.product) {
      acc.products.push({
        id: curr.product.id,
        label: curr.product.title
      });
    }
    if ("student" in curr) {
      const hasAddedStudent = acc.students.some(it => it.id === curr.student?.id);
      const hasAddedTeam = acc.teams.some(it => it.id === curr.student?.team);
      if (!hasAddedStudent && curr.student) {
        acc.students.push({
          id: curr.student.id,
          label: curr.student.name
        });
      }
      if (!hasAddedTeam && curr.student) {
        acc.teams.push({
          id: curr.student.team,
          label: curr.student.team
        });
      }
    }
    return acc;
  }, {
    students: [],
    teams: [],
    products: []
  }), [data]);

  // When changing timespan, the list of available options for the other
  // filters may change. Ensure that all entries from other fitlers that are no
  // longer valid after timespan change is removed.
  useEffect(() => {
    const missingStudentIds = Object.keys(filters.studentIds).filter(filteredStudentId => !data?.map(entry => "student" in entry && entry.student?.id).includes(filteredStudentId));
    for (const missingStudentId of missingStudentIds) {
      componentEntryStore.dispatch(componentEntryStore.actions.filterByStudent(missingStudentId));
    }
    const missingStudentTeams = Object.keys(filters.teamIds).filter(filteredStudentName => !data?.map(entry => "student" in entry && entry.student?.team).includes(filteredStudentName));
    for (const missingStudentTeam of missingStudentTeams) {
      componentEntryStore.dispatch(componentEntryStore.actions.filterByTeam(missingStudentTeam));
    }
    const missingProducts = Object.keys(filters.productIds).filter(filteredProduct => !data?.map(entry => entry.product?.id).includes(filteredProduct));
    for (const missingProduct of missingProducts) {
      componentEntryStore.dispatch(componentEntryStore.actions.filterByProduct(missingProduct));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const showThrobber = useLoaderState(isLoading || isRefetching);
  return {
    options,
    filters,
    showTimespanThrobber: showThrobber,
    isLoadingTimespan: isLoading || isRefetching
  };
}

export { useFilterValues };
//# sourceMappingURL=hook.filters.js.map
